import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '../lib/api';
import type { SessionUser } from '../lib/types';

interface AuthValue {
  user: SessionUser | null;
  carregando: boolean;
  entrar: (input: {
    email: string;
    senha: string;
    lembrar: boolean;
  }) => Promise<SessionUser>;
  sair: () => Promise<void>;
  /** Relê `/api/auth/me`; usada depois de trocar dados do perfil. */
  recarregar: () => Promise<void>;
  /** Actualiza o utilizador em memória sem ir ao servidor. */
  definirUsuario: (user: SessionUser | null) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [carregando, setCarregando] = useState(true);
  const queryClient = useQueryClient();

  const recarregar = useCallback(async () => {
    try {
      const res = await api<{ user: SessionUser | null }>('/auth/me');
      setUser(res.user);
    } catch (error) {
      // Uma falha de rede não deve deslogar quem já estava dentro; só um 401 o faz.
      if (error instanceof ApiError && error.isUnauthorized) setUser(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const entrar = useCallback<AuthValue['entrar']>(async (input) => {
    const res = await api<{ user: SessionUser }>('/auth/login', {
      method: 'POST',
      body: input,
    });
    setUser(res.user);
    return res.user;
  }, []);

  const sair = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
      // Sem isto, o próximo utilizador a entrar no mesmo navegador veria por
      // um instante os baralhos e as estatísticas do anterior.
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthValue>(
    () => ({ user, carregando, entrar, sair, recarregar, definirUsuario: setUser }),
    [user, carregando, entrar, sair, recarregar],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa de estar dentro de <AuthProvider>.');
  return ctx;
}

/** Conveniência para as páginas que só existem depois do login. */
export function useUsuario(): SessionUser {
  const { user } = useAuth();
  if (!user) throw new Error('Página protegida usada sem sessão.');
  return user;
}
